import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, LogOut, Camera, Save, BadgeCheck, Upload, Loader2,
  Globe, Instagram, Facebook, Linkedin, Mail, Phone, MapPin,
  Briefcase, Calendar, Users as UsersIcon, IndianRupee, Send, Handshake,
  LocateFixed,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import BottomNav from "@/components/BottomNav";
import BrandSubscriptionCard from "@/components/BrandSubscriptionCard";
import { useAuth, API } from "@/App";

const NICHES = [
  "Fashion", "Tech", "Food", "Fitness", "Beauty", "Travel",
  "Lifestyle", "Entertainment", "Education", "E-commerce",
  "Gaming", "Health", "Finance", "Automotive", "Home & Decor",
];

const TEAM_SIZES = ["Just me", "1-10", "11-50", "51-200", "200+"];
const BUSINESS_TYPES = ["Startup", "SME", "Enterprise", "Agency", "D2C Brand", "Service Provider"];

const currentYear = new Date().getFullYear();

const BrandProfile = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    niche: "Fashion",
    tagline: "",
    description: "",
    picture: "",
    // Contact & links
    website_url: "",
    instagram_url: "",
    facebook_url: "",
    linkedin_url: "",
    contact_email: "",
    contact_phone: "",
    // Company info
    city: "",
    state: "",
    founded_year: "",
    team_size: "",
    business_type: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (formData.picture) {
      loadProfileImage(formData.picture);
    }
    return () => {
      if (profileImageUrl && profileImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.picture]);

  const loadProfileImage = async (picturePath) => {
    if (!picturePath) {
      setProfileImageUrl(null);
      return;
    }
    if (picturePath.startsWith("http")) {
      setProfileImageUrl(picturePath);
    } else {
      try {
        const response = await axios.get(`${API}/files/${picturePath}`, {
          responseType: "blob",
          headers: getAuthHeaders(),
        });
        const blobUrl = URL.createObjectURL(response.data);
        setProfileImageUrl(blobUrl);
      } catch (error) {
        console.error("Failed to load image:", error);
        setProfileImageUrl(null);
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/brand/profile`, {
        headers: getAuthHeaders(),
      });
      const p = response.data || {};
      setProfile(p);
      // Infer instagram_url from legacy social_media_url if it looks like insta
      const legacySocial = p.social_media_url || "";
      setFormData({
        name: p.name || "",
        niche: p.niche || "Fashion",
        tagline: p.tagline || "",
        description: p.description || "",
        picture: p.picture || "",
        website_url: p.website_url || "",
        instagram_url: p.instagram_url || (legacySocial && legacySocial.toLowerCase().includes("instagram") ? legacySocial : ""),
        facebook_url: p.facebook_url || (legacySocial && legacySocial.toLowerCase().includes("facebook") ? legacySocial : ""),
        linkedin_url: p.linkedin_url || (legacySocial && legacySocial.toLowerCase().includes("linkedin") ? legacySocial : ""),
        contact_email: p.contact_email || p.email || "",
        contact_phone: p.contact_phone || "",
        city: p.city || "",
        state: p.state || "",
        founded_year: p.founded_year ? String(p.founded_year) : "",
        team_size: p.team_size || "",
        business_type: p.business_type || "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      const response = await axios.post(`${API}/upload/profile-photo`, uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders(),
        },
      });
      setFormData((prev) => ({ ...prev, picture: response.data.path }));
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const validateURL = (v) => {
    if (!v) return true;
    try {
      const u = new URL(v.startsWith("http") ? v : `https://${v}`);
      return !!u.host;
    } catch {
      return false;
    }
  };

  const normalizeURL = (v) => {
    if (!v) return v;
    return v.startsWith("http") ? v : `https://${v}`;
  };

  const handleSave = async () => {
    // Light validation
    const urls = [
      ["Website URL", formData.website_url],
      ["Instagram URL", formData.instagram_url],
      ["Facebook URL", formData.facebook_url],
      ["LinkedIn URL", formData.linkedin_url],
    ];
    for (const [label, v] of urls) {
      if (v && !validateURL(v)) {
        toast.error(`Please enter a valid ${label}`);
        return;
      }
    }
    if (formData.founded_year) {
      const y = parseInt(formData.founded_year);
      if (isNaN(y) || y < 1800 || y > currentYear) {
        toast.error(`Founded year must be between 1800 and ${currentYear}`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name?.trim(),
        niche: formData.niche,
        tagline: formData.tagline?.trim(),
        description: formData.description?.trim(),
        picture: formData.picture || null,
        website_url: formData.website_url ? normalizeURL(formData.website_url.trim()) : "",
        instagram_url: formData.instagram_url ? normalizeURL(formData.instagram_url.trim()) : "",
        facebook_url: formData.facebook_url ? normalizeURL(formData.facebook_url.trim()) : "",
        linkedin_url: formData.linkedin_url ? normalizeURL(formData.linkedin_url.trim()) : "",
        contact_email: formData.contact_email?.trim() || "",
        contact_phone: formData.contact_phone?.trim() || "",
        city: formData.city?.trim() || "",
        state: formData.state?.trim() || "",
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null,
        team_size: formData.team_size || "",
        business_type: formData.business_type || "",
      };
      const response = await axios.put(`${API}/brand/profile`, payload, {
        headers: getAuthHeaders(),
      });
      setProfile(response.data);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not supported on this device");
      return;
    }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Reverse geocode using OpenStreetMap Nominatim (free, no API key)
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&accept-language=en`,
            { headers: { "Accept": "application/json" } }
          );
          const data = await res.json();
          const addr = data?.address || {};
          const detectedCity =
            addr.city || addr.town || addr.village || addr.suburb || addr.county || addr.state_district || "";
          const detectedState = addr.state || "";
          if (!detectedCity && !detectedState) {
            toast.error("Couldn't detect a city. Try again or enter manually.");
          } else {
            setFormData((prev) => ({
              ...prev,
              city: detectedCity || prev.city,
              state: detectedState || prev.state,
            }));
            toast.success(`Detected: ${detectedCity}${detectedState ? ", " + detectedState : ""}`);
          }
        } catch (e) {
          console.error(e);
          toast.error("Failed to fetch your location details");
        } finally {
          setDetectingLocation(false);
        }
      },
      (err) => {
        setDetectingLocation(false);
        if (err.code === 1) {
          toast.error("Location permission denied. Enable it in browser settings.");
        } else {
          toast.error("Couldn't fetch your location. Please try again.");
        }
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF69B4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const stats = [
    { label: "Requests Sent", value: profile?.requests_sent || 0, icon: Send, color: "text-[#7B2FBE]", bg: "bg-[#7B2FBE]/10" },
    { label: "Accepted", value: profile?.approved_requests || 0, icon: Handshake, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
    { label: "Closed Deals", value: profile?.closed_deals || 0, icon: BadgeCheck, color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/10" },
    { label: "₹ Spent", value: `₹${(profile?.money_spent || 0).toLocaleString("en-IN")}`, icon: IndianRupee, color: "text-[#FF69B4]", bg: "bg-[#FF69B4]/10" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Brand Profile</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Hero — Logo + name + approval badge + stats */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <Avatar className="w-28 h-28 border-4 border-[#E2E8F0]">
                <AvatarImage src={profileImageUrl} alt={formData.name} />
                <AvatarFallback className="bg-[#F0F9FF] text-[#87CEEB] font-outfit font-bold text-3xl">
                  {formData.name?.charAt(0) || "B"}
                </AvatarFallback>
              </Avatar>
              {profile?.is_verified && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center">
                  <BadgeCheck className="w-5 h-5 text-white" />
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                ) : (
                  <Camera className="w-8 h-8 text-white" />
                )}
              </button>
            </div>

            <h2 className="font-outfit font-semibold text-xl text-[#1E293B]">{formData.name || "Your Brand"}</h2>
            {formData.tagline && (
              <p className="text-sm text-[#64748B] mt-1 text-center max-w-md">{formData.tagline}</p>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
              {profile?.is_verified && (
                <span className="text-xs bg-[#87CEEB]/15 text-[#1E3A8A] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3" /> Verified Brand
                </span>
              )}
              {profile?.approval_status === "pending" && (
                <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full">
                  ⏳ Admin approval pending
                </span>
              )}
              {profile?.approval_status === "approved" && (
                <span className="text-xs bg-emerald-100 text-emerald-800 font-semibold px-2 py-0.5 rounded-full">
                  ✅ Approved
                </span>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
              data-testid="brand-logo-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-4 rounded-full"
              data-testid="upload-logo-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload Brand Logo</>
              )}
            </Button>
            <p className="text-xs text-[#64748B] mt-2">Max size: 5MB (JPG, PNG, WebP)</p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-[#F1F5F9]">
            {stats.map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                <div className="flex items-center justify-center mb-1">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <p className={`font-outfit font-bold text-lg ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-[#64748B] uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Basic Info Section */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-[#1E293B] mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#7B2FBE]" />
            Basic Info
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-[#1E293B]">Brand Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 rounded-xl"
                placeholder="Your brand name"
                data-testid="brand-name-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">Industry / Niche</Label>
              <Select value={formData.niche} onValueChange={(v) => setFormData({ ...formData, niche: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="brand-niche-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#1E293B]">Tagline</Label>
              <Input
                placeholder="Your brand's catchy tagline"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="mt-1 rounded-xl"
                data-testid="brand-tagline-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">About Your Brand</Label>
              <Textarea
                placeholder="Tell stars about your brand, products, and what kind of collabs you're looking for..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 rounded-xl min-h-[120px]"
                data-testid="brand-description-input"
                maxLength={1000}
              />
              <p className="text-[10px] text-right text-[#94A3B8] mt-1">{formData.description.length}/1000</p>
            </div>
          </div>
        </div>

        {/* Contact & Web Links */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-[#1E293B] mb-1 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#7B2FBE]" />
            Contact &amp; Web Links
          </h3>
          <p className="text-xs text-[#64748B] mb-4">Help stars verify you — add your website and socials.</p>

          <div className="space-y-3">
            <LinkField
              icon={<Globe className="w-4 h-4" />}
              label="Website URL"
              placeholder="https://yourbrand.com"
              value={formData.website_url}
              onChange={(v) => setFormData({ ...formData, website_url: v })}
              testId="brand-website-input"
            />
            <LinkField
              icon={<Instagram className="w-4 h-4" />}
              label="Instagram"
              placeholder="https://instagram.com/yourbrand"
              value={formData.instagram_url}
              onChange={(v) => setFormData({ ...formData, instagram_url: v })}
              testId="brand-instagram-input"
            />
            <LinkField
              icon={<Facebook className="w-4 h-4" />}
              label="Facebook"
              placeholder="https://facebook.com/yourbrand"
              value={formData.facebook_url}
              onChange={(v) => setFormData({ ...formData, facebook_url: v })}
              testId="brand-facebook-input"
            />
            <LinkField
              icon={<Linkedin className="w-4 h-4" />}
              label="LinkedIn"
              placeholder="https://linkedin.com/company/yourbrand"
              value={formData.linkedin_url}
              onChange={(v) => setFormData({ ...formData, linkedin_url: v })}
              testId="brand-linkedin-input"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <LinkField
                icon={<Mail className="w-4 h-4" />}
                label="Contact Email"
                placeholder="hello@yourbrand.com"
                type="email"
                value={formData.contact_email}
                onChange={(v) => setFormData({ ...formData, contact_email: v })}
                testId="brand-contact-email-input"
              />
              <LinkField
                icon={<Phone className="w-4 h-4" />}
                label="Contact / WhatsApp"
                placeholder="+91 98765 43210"
                value={formData.contact_phone}
                onChange={(v) => setFormData({ ...formData, contact_phone: v })}
                testId="brand-contact-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold text-lg text-[#1E293B] flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-[#7B2FBE]" />
              Company Info
            </h3>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleDetectLocation}
              disabled={detectingLocation}
              className="rounded-full text-xs border-[#FF69B4] text-[#FF69B4] hover:bg-[#FF69B4]/5"
              data-testid="brand-detect-location-btn"
            >
              {detectingLocation ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Detecting...</>
              ) : (
                <><LocateFixed className="w-3 h-3 mr-1" /> Detect my location</>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[#1E293B] flex items-center gap-1"><MapPin className="w-3 h-3" /> City</Label>
              <Input
                placeholder="Mumbai"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="mt-1 rounded-xl"
                data-testid="brand-city-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">State</Label>
              <Input
                placeholder="Maharashtra"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="mt-1 rounded-xl"
                data-testid="brand-state-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B] flex items-center gap-1"><Calendar className="w-3 h-3" /> Founded Year</Label>
              <Input
                type="number"
                min="1800"
                max={currentYear}
                placeholder="2020"
                value={formData.founded_year}
                onChange={(e) => setFormData({ ...formData, founded_year: e.target.value })}
                className="mt-1 rounded-xl"
                data-testid="brand-founded-input"
              />
            </div>
            <div>
              <Label className="text-[#1E293B]">Team Size</Label>
              <Select
                value={formData.team_size}
                onValueChange={(v) => setFormData({ ...formData, team_size: v })}
              >
                <SelectTrigger className="mt-1 rounded-xl" data-testid="brand-team-size-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#1E293B]">Business Type</Label>
              <Select
                value={formData.business_type}
                onValueChange={(v) => setFormData({ ...formData, business_type: v })}
              >
                <SelectTrigger className="mt-1 rounded-xl" data-testid="brand-business-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mb-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] hover:opacity-90 text-white rounded-full py-6 shadow-lg font-semibold"
            data-testid="brand-save-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Profile</>
            )}
          </Button>
        </div>

        {/* Brand Pro Subscription Card — at the BOTTOM as requested */}
        <BrandSubscriptionCard />
      </main>

      <BottomNav userType="brand" />
    </div>
  );
};

/** Reusable input row with a leading icon */
function LinkField({ icon, label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <Label className="text-[#1E293B]">{label}</Label>
      <div className="relative mt-1">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B2FBE] pointer-events-none">
          {icon}
        </div>
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="rounded-xl pl-9"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

export default BrandProfile;
